import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { TabContainer } from '../../styles';
import { Button, Input, Toggle, TopProgress } from '../../../../components';
import { authService } from '../../../../api';
import { useUi } from '../../../../context/ui.context';

const Notifications: FC = () => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    handleSubmit,
    setErrors,
    setValues,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      low_balance_notification: false,
      low_balance_notification_amount: 0,
    },
    validationSchema: yup.object().shape({
      low_balance_notification: yup.boolean().required(),
      low_balance_notification_amount: yup
        .number()
        .typeError(t('REQUIRED_FIELD'))
        .min(0, t('REQUIRED_FIELD'))
        .when('low_balance_notification', {
          is: true,
          then: schema => schema.required(t('REQUIRED_FIELD')),
        }),
    }),
    onSubmit: async (formValues) => {
      setSaving(true);
      try {
        await authService.updateLowBalanceNotification({
          low_balance_notification: formValues.low_balance_notification,
          low_balance_notification_amount: Number(formValues.low_balance_notification_amount),
        });
        viewAlert({
          severity: 'success',
          message: t('SETTINGS_SAVED', { defaultValue: 'Settings saved' }),
        });
      } catch (err: any) {
        if (err?.response?.data) {
          setErrors(err.response.data);
          const detail = err.response.data.detail;
          if (detail) {
            viewAlert({ severity: 'error', message: detail });
          }
        }
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    authService.getLowBalanceNotification()
      .then((res: any) => {
        setValues({
          low_balance_notification: !!res.data.low_balance_notification,
          low_balance_notification_amount: res.data.low_balance_notification_amount ?? 0,
        });
      })
      .catch((err: any) => {
        if (err?.response?.data?.detail) {
          viewAlert({
            severity: 'error',
            message: err.response.data.detail,
          });
        }
      })
      .finally(() => setPageLoading(false));
  }, [setValues, viewAlert]);

  if (pageLoading) return <TopProgress loading={pageLoading} />;

  const formikProps = {
    errors,
    values,
    touched,
    handleChange,
    handleBlur,
  };

  return (
    <TabContainer>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems:'flex-start', gap: '16px', maxWidth: '420px' }}>
        <Toggle
          title={t('LOW_BALANCE_NOTIFICATION', { defaultValue: 'Low balance notification' })}
          value={values.low_balance_notification}
          onChange={() => setFieldValue('low_balance_notification', !values.low_balance_notification)}
        />
        <Input
          fieldName="low_balance_notification_amount"
          title={t('LOW_BALANCE_THRESHOLD', { defaultValue: 'Notification threshold amount' })}
          placeholder=""
          type="number"
          {...formikProps}
          disabled={!values.low_balance_notification}
        />
        <Button
          onClick={() => handleSubmit()}
          focusOnValidationError
          variant="primary"
          size="display"
          loading={saving}
          disabled={saving}
          title={t('SAVE', { defaultValue: 'Save' })}
        />
      </div>
    </TabContainer>
  );
};

export default Notifications;
